import { useQuery } from '@tanstack/react-query'
import { dueCardsApi } from '@/api/dueCards'
import { notificationsApi } from '@/api/notifications'
import { videoListApi } from '@/api/videoList'
import { useAuth } from '@/features/auth/useAuth'
import { isAdminLike, isCustomer, isSubAdmin, isTeamLead } from '@/features/auth/roles'
import { dayjs, toIsoDate } from '@/lib/dates'

export interface NavBadges {
  videoList?: number
  dueToday?: number
  inbox?: number
  ideas?: number
}

/** Live counts shown next to sidebar items. */
export function useNavBadges(): NavBadges {
  const { user } = useAuth()
  const staff = !!user && !isCustomer(user)
  const canSeeInbox = staff && !isSubAdmin(user)
  const today = toIsoDate(dayjs())
  const inbox = useQuery({
    queryKey: ['inbox', 'unseen'],
    queryFn: notificationsApi.inboxUnseen,
    enabled: canSeeInbox,
    refetchInterval: 60 * 1000,
  })
  const dueToday = useQuery({
    queryKey: ['stats', 'due-today', today],
    queryFn: () => dueCardsApi.byDate({ dueDate: today, viewAsCardType: 'due_date' }),
    enabled: staff,
    staleTime: 60 * 1000,
  })
  const videoList = useQuery({
    queryKey: ['videoList', 'current_month', ''],
    queryFn: () => videoListApi.list({ time_period: 'current_month' }),
    enabled: !!user && (isAdminLike(user) || isTeamLead(user)),
    staleTime: 5 * 60 * 1000,
  })
  return {
    inbox: inbox.data?.length || undefined,
    dueToday: dueToday.data?.length || undefined,
    // Defensive on purpose: a malformed response must not take the whole app shell down.
    videoList: videoList.data?.cards?.length || undefined,
  }
}
